import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { Search, Filter, ChevronRight } from "lucide-react";
import { toast } from "react-toastify";
import { adminServices } from "../../services/AdminServices";
import EditApprovalProductModal from "../../components/EditApprovalProductModal";

const ItemApproval = ({ onBack, data = [], loading = false, categories = [], classifications = [], subclassifications = [] }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailItems, setDetailItems] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailReason, setDetailReason] = useState("");
  const [detailBucorRemarks, setDetailBucorRemarks] = useState("");
  const [selectedDetailIds, setSelectedDetailIds] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [isEditProductModalOpen, setIsEditProductModalOpen] = useState(false);

  const handleRowClick = (row) => {
    const p = row.originalData || {};
    const subclass = p.subclassfication || {};
    // Strip "-" placeholder values so modal fields open empty
    const clean = (v) => (v === "-" || v == null ? "" : v);
    setEditProduct({
      ...row,
      originalData: p,
      id: p.id ?? row.productId,
      sku: clean(p.sku ?? row.sku),
      barcode: clean(p.barcode ?? row.code),
      description: clean(p.name ?? p.details ?? row.description),
      category: clean(subclass.productCategory?.category ?? subclass.productCategory?.name ?? row.category),
      classification: clean(subclass.name ?? row.classification),
      subCategory: clean(row.subCategory),
      brand: clean(p.brand ?? row.brand),
      variant: clean(p.variant ?? row.size),
      largestUOM: clean(p.largestUOM ?? p.largest_uom ?? row.largestUOM),
      smallestUOM: clean(p.smallestUOM ?? p.smallest_uom ?? ""),
      marketPrice: clean(p.marketPrice ?? ""),
      profitMargin: clean(p.profitMargin ?? "")
    });
    setIsEditProductModalOpen(true);
  };

  const handleSaveProduct = async (form) => {
    try {
      const classificationName = classifications?.find((c) => c.id === Number(form.productClassificationId))?.name ?? "";
      const subClassificationName = subclassifications?.find((sc) => sc.id === Number(form.productSubclassificationId))?.name ?? "";
      const payload = {
        productId: Number(form.productId) || 0,
        sku: form.sku ?? "",
        barcode: form.barcode ?? "",
        itemTag: form.itemTag ?? "",
        name: form.name ?? form.details ?? "",
        brand: form.brand ?? "",
        variant: form.variant ?? "",
        details: form.details ?? "",
        category: form.category ?? "",
        classification: classificationName,
        subClassification: subClassificationName,
        markup: Number(form.markup) || 0,
        marketPrice: Number(form.marketPrice) || 0,
        supplierToIpxUom: form.supplierToIpxUom ?? "",
        supplierToIpxPrice: Number(form.supplierToIpxPrice) || 0,
        ipxToBigStoreUom: form.ipxToBigStoreUom ?? "",
        ipxToBigStorePrice: Number(form.ipxToBigStorePrice) || 0,
        ipxToSmallStoreUom: form.ipxToSmallStoreUom ?? "",
        ipxToSmallStorePrice: Number(form.ipxToSmallStorePrice) || 0,
        ipxToPdlUom: form.ipxToPdlUom ?? "",
        ipxToPdlPrice: Number(form.ipxToPdlPrice) || 0,
        bigStoreToSmallStoreUom: form.bigStoreToSmallStoreUom ?? "",
        bigStoreToSmallStorePrice: Number(form.bigStoreToSmallStorePrice) || 0,
        bigStoreToPdlUom: form.bigStoreToPdlUom ?? "",
        bigStoreToPdlPrice: Number(form.bigStoreToPdlPrice) || 0,
        smallStoreToPdlUom: form.smallStoreToPdlUom ?? "",
        smallStoreToPdlPrice: Number(form.smallStoreToPdlPrice) || 0
      };
      await adminServices.updateProduct(payload);
      toast.success("Product updated successfully.");
      setIsEditProductModalOpen(false);
      setEditProduct(null);
      fetchDetails();
    } catch (error) {
      console.error("Error updating product:", error);
      toast.error(error.response?.data?.message || "Failed to update product.");
      throw error;
    }
  };

  const columns = [
    {
      name: "Request Number",
      selector: row => row.requestNumber,
      sortable: true,
      center: true
    },
    {
      name: "Requested by",
      selector: row => row.requestedBy,
      sortable: true,
      center: true
    },
    {
      name: "No. of Items",
      selector: row => row.numberOfItems,
      sortable: true,
      center: true
    },
    {
      name: "Status",
      selector: row => row.status,
      cell: row => {
        if (row.status === "-") return "-";
        const s = String(row.status || "").toLowerCase();
        const statusClass =
          s === "approved" ? "item-approval-status-approved" :
          s === "rejected" ? "item-approval-status-rejected" :
          s === "pending bucor approval" ? "item-approval-status-pending" :
          "item-approval-status-pending";
        return (
          <span className={`item-approval-status-badge ${statusClass}`} style={{ textAlign: "center" }}>
            {row.status}
          </span>
        );
      },
      sortable: true,
      center: true
    },
    {
      name: "Reason for listing",
      selector: row => row.remarks,
      sortable: true,
      center: true
    },
    {
      name: "BuCor Remarks",
      selector: row => row.bucorRemarks ?? "-",
      sortable: true,
      center: true
    },
    {
      name: "Action",
      selector: () => "",
      cell: row => (
        <button 
          className="item-approval-view-btn"
          onClick={() => handleView(row)}
        >
          View
        </button>
      ),
      sortable: false,
      center: true
    }
  ];

  const handleView = (row) => {
    setSelectedRequest(row);
  };

  const handleBackToTable = () => {
    setSelectedRequest(null);
  };

  const baseData = Array.isArray(data) && data.length > 0 ? data : [];

  const filteredData = baseData.filter(item => {
    const query = searchQuery.toLowerCase();
    const matchesSearch = !searchQuery || (
      item.requestNumber.toLowerCase().includes(query) ||
      item.requestedBy.toLowerCase().includes(query) ||
      item.remarks.toLowerCase().includes(query)
    );
    const matchesStatus = statusFilter === "All" || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#002347",
        color: "#ffffff",
        fontSize: "0.875rem",
        fontWeight: "600"
      }
    },
    headCells: {
      style: {
        color: "#ffffff",
        fontSize: "0.875rem",
        fontWeight: "600",
        justifyContent: "center"
      }
    },
    cells: {
      style: {
        fontSize: "0.875rem",
        color: "#111827",
        justifyContent: "center"
      }
    }
  };

  const fetchDetails = async (request) => {
    const req = request ?? selectedRequest;
    if (!req || !req.id) return;
    try {
      setDetailLoading(true);
      const res = await adminServices.getData(`ItemApprovals/${req.id}`);
      const data = res?.data || {};
      const items = Array.isArray(data.merchantItemRequestItems)
        ? data.merchantItemRequestItems
        : [];
      const mapped = items.map((entry, index) => {
        const p = entry.product || {};
        const subclass = p.subclassfication || {};
        const category =
          subclass.productCategory?.category ??
          subclass.productCategory?.name ??
          "-";
        return {
          id: entry.id ?? index + 1,
          productId: p.id ?? entry.productId,
          sku: p.sku || "-",
          code: p.barcode || "-",
          description: p.name || p.details || "-",
          category,
          classification: subclass.name || "-",
          subCategory: "-",
          brand: p.brand || "-",
          size: p.variant || "-",
          largestUOM: "-",
          originalData: p
        };
      });
      setDetailItems(mapped);
      setDetailReason(data.reasonForListing || "");
      setDetailBucorRemarks(data.bucorRemarks || "");
      setSelectedDetailIds([]);
    } catch (err) {
      console.error("Error fetching item approval details:", err);
      setDetailItems([]);
      setDetailReason("");
      setDetailBucorRemarks("");
      setSelectedDetailIds([]);
    } finally {
      setDetailLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchDetails(); }, [selectedRequest]);

  // If a request is selected, show detail view
  if (selectedRequest) {
    return (
      <div className="dashboard-content">
        <div className="item-approval-page">
          {/* Breadcrumbs */}
          <div className="item-approval-breadcrumbs">
            <span className="item-approval-breadcrumb-item" onClick={onBack}>Item Master</span>
            <ChevronRight size={16} className="item-approval-breadcrumb-separator" />
            <span className="item-approval-breadcrumb-item" onClick={handleBackToTable}>Item Approval</span>
            <ChevronRight size={16} className="item-approval-breadcrumb-separator" />
            <span className="item-approval-breadcrumb-item active">{selectedRequest.requestNumber}</span>
          </div>

          {/* Item Details Table */}
          <div className="item-approval-detail-table-container">
            <table className="item-approval-detail-table">
              <thead>
                <tr>
                  <th style={{ textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={
                        detailItems.length > 0 &&
                        selectedDetailIds.length === detailItems.length
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDetailIds(detailItems.map((it) => it.id));
                        } else {
                          setSelectedDetailIds([]);
                        }
                      }}
                    />
                  </th>
                  <th style={{ textAlign: "center" }}>SKU</th>
                  <th style={{ textAlign: "center" }}>Barcode</th>
                  <th style={{ textAlign: "center" }}>Description</th>
                  <th style={{ textAlign: "center" }}>Brand</th>
                  <th style={{ textAlign: "center" }}>Size</th>
                </tr>
              </thead>
              <tbody>
                {detailLoading ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", padding: "16px" }}>
                      Loading items...
                    </td>
                  </tr>
                ) : detailItems.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", padding: "16px" }}>
                      No items found for this request.
                    </td>
                  </tr>
                ) : (
                  detailItems.map((item, index) => (
                    <tr
                      key={item.id}
                      className={index === 1 ? "item-approval-detail-row-highlight" : ""}
                      onClick={() => handleRowClick(item)}
                      style={{ cursor: "pointer" }}
                    >
                      <td style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedDetailIds.includes(item.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedDetailIds((prev) =>
                                prev.includes(item.id)
                                  ? prev
                                  : [...prev, item.id]
                              );
                            } else {
                              setSelectedDetailIds((prev) =>
                                prev.filter((id) => id !== item.id)
                              );
                            }
                          }}
                        />
                      </td>
                      <td style={{ textAlign: "center" }}>{item.sku}</td>
                      <td style={{ textAlign: "center" }}>{item.code}</td>
                      <td style={{ textAlign: "center" }}>{item.description}</td>
                      <td style={{ textAlign: "center" }}>{item.brand}</td>
                      <td style={{ textAlign: "center" }}>{item.size}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Submit for Approval Button */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              padding: "24px 0 8px",
            }}
          >
            <button
              type="button"
              className="item-approval-submit-btn"
              disabled={submitLoading || selectedDetailIds.length === 0}
              onClick={async () => {
                if (!selectedRequest) return;
                try {
                  setSubmitLoading(true);
                  await adminServices.updateItemApprovalStatus(
                    selectedRequest.id,
                    "PENDING BUCOR APPROVAL",
                    detailReason || ""
                  );
                  toast.success("Item approval submitted successfully.");
                  setSelectedDetailIds([]);
                  setSelectedRequest(null);
                } catch (err) {
                  console.error("Failed to submit item approval:", err);
                  toast.error(err?.response?.data?.message || "Failed to submit item approval.");
                } finally {
                  setSubmitLoading(false);
                }
              }}
              style={{
                minWidth: 220,
                height: 44,
                borderRadius: 8,
                border: "none",
                padding: "0 24px",
                backgroundColor: "#002347",
                color: "#ffffff",
                fontSize: "0.95rem",
                fontWeight: 600,
                cursor:
                  submitLoading || selectedDetailIds.length === 0
                    ? "not-allowed"
                    : "pointer",
                opacity:
                  submitLoading || selectedDetailIds.length === 0
                    ? 0.6
                    : 1,
              }}
            >
              {submitLoading ? "Submitting..." : "Submit for Approval"}
            </button>
          </div>

          {/* Reason for listing (optional) */}
          <div
            className="item-approval-reason-section"
            style={{
              marginTop: 8,
              padding: 20,
              backgroundColor: "#F9FAFB",
              borderRadius: 12,
            }}
          >
            <h3
              className="item-approval-remarks-title"
              style={{ marginBottom: 8, fontSize: "0.9rem", color: "#374151" }}
            >
              Reason for listing (optional)
            </h3>
            <textarea
              className="item-approval-remarks-textarea"
              placeholder="Reason for listing"
              rows={4}
              value={detailReason}
              onChange={(e) => setDetailReason(e.target.value)}
              style={{
                width: "100%",
                borderRadius: 8,
                border: "1px solid #E5E7EB",
                padding: "10px 12px",
                fontSize: "0.9rem",
                resize: "vertical",
                minHeight: 96,
              }}
            />
          </div>

          {/* BuCor Remarks Section */}
          <div
            className="item-approval-remarks-section"
            style={{
              marginTop: 16,
              padding: 20,
              backgroundColor: "#F9FAFB",
              borderRadius: 12,
            }}
          >
            <div
              className="item-approval-remarks-header"
              style={{ display: "flex", alignItems: "center", gap: 12 }}
            >
              <h3
                className="item-approval-remarks-title"
                style={{ fontSize: "0.9rem", color: "#374151" }}
              >
                BuCor Remarks
              </h3>
            </div>
            <textarea
              className="item-approval-remarks-textarea"
              placeholder="BuCor remarks"
              rows={6}
              value={detailBucorRemarks}
              readOnly
              style={{
                width: "100%",
                borderRadius: 8,
                border: "1px solid #E5E7EB",
                padding: "10px 12px",
                fontSize: "0.9rem",
                resize: "none",
                minHeight: 120,
                marginTop: 10,
                backgroundColor: "#f3f4f6",
                cursor: "not-allowed",
              }}
            />
          </div>
        </div>

        <EditApprovalProductModal
          product={editProduct}
          isOpen={isEditProductModalOpen}
          onClose={() => { setIsEditProductModalOpen(false); setEditProduct(null); }}
          onSave={handleSaveProduct}
          categories={categories}
          classifications={classifications}
          subclassifications={subclassifications}
        />
      </div>
    );
  }

  return (
    <div className="dashboard-content">
      <div className="item-approval-page">
        {/* Breadcrumbs */}
        <div className="item-approval-breadcrumbs">
          <span className="item-approval-breadcrumb-item" onClick={onBack}>Item Master</span>
          <ChevronRight size={16} className="item-approval-breadcrumb-separator" />
          <span className="item-approval-breadcrumb-item active">Item Approval</span>
        </div>

        {/* Search and Filters */}
        <div className="item-approval-search-filter">
          <div className="item-approval-search-container">
            <Search className="item-approval-search-icon" size={18} />
            <input
              type="text"
              className="item-approval-search-input"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="item-approval-filter-btn" style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 12px", cursor: "default" }}>
            <Filter size={18} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                border: "none",
                background: "transparent",
                fontSize: "0.875rem",
                fontWeight: 500,
                cursor: "pointer",
                outline: "none",
                color: "inherit"
              }}
            >
              <option value="All">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Pending Bucor Approval">Pending Bucor Approval</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="item-approval-table-container">
          <DataTable
            columns={columns}
            data={filteredData}
            customStyles={customStyles}
            pagination={false}
            highlightOnHover
          />
        </div>
      </div>
    </div>
  );
};

export default ItemApproval;

